import { Inject, Injectable } from '@angular/core';
import {
  API_SERVICE_CONFIG,
  IApiServiceConfig,
} from '../base/configuration/api-service-config';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import {
  MiniWorkOrder,
  OverlappingWorkOrder,
  WorkOrder,
  WorkOrderAssignee,
  WorkOrderStatus,
} from './work-order';
import { ResponseWrapper } from '../base/entities/responses/response-wrapper';
import { isSingleErrorCode } from '@fullyops/shared/is-single-error-code';

@Injectable({ providedIn: 'root' })
export class ApiWorkOrdersAdapterService {
  constructor(
    private http: HttpClient,
    @Inject(API_SERVICE_CONFIG) private config: IApiServiceConfig,
  ) {}

  get(): Observable<WorkOrder[]> {
    return this.http.get<WorkOrder[]>(
      this.config.url + 'tickets/multimachinesupports/board-items',
    );
  }

  getOpenNames(): Observable<MiniWorkOrder[]> {
    return this.http.get<MiniWorkOrder[]>(
      this.config.url + 'tickets/multimachinesupports/open-names',
    );
  }

  getPotentialAssignees(): Observable<WorkOrderAssignee[]> {
    // Given the long legacy of work orders as support tickets,
    // any user could potentially be an assignee here.
    return this.http.get<WorkOrderAssignee[]>(this.config.url + 'users/names');
  }

  getStatuses(): Observable<WorkOrderStatus[]> {
    return this.http
      .get<
        ResponseWrapper<WorkOrderStatus>
      >(this.config.url + 'tickets/support/status')
      .pipe(map((res) => res.results));
  }

  getOverlaps(
    assigneeIds: string[],
    startDate: string,
    endDate: string | null,
  ): Observable<OverlappingWorkOrder[]> {
    if (assigneeIds.length === 0 || (startDate == null && endDate == null)) {
      return of([]);
    }
    const queryParams: any = { assignees: assigneeIds };
    if (startDate != null) {
      queryParams.startDate = startDate;
    }
    if (endDate != null) {
      queryParams.endDate = endDate;
    }
    return this.http.get<OverlappingWorkOrder[]>(
      this.config.url + 'work-orders/overlapping',
      { params: queryParams },
    );
  }

  updateStatus(id: string, newStatusId: string): Observable<void> {
    return this.update(id, { statusId: newStatusId });
  }

  updateInterval(
    id: string,
    interval: { start: string; end: string },
    allDay: boolean,
  ): Observable<void> {
    return this.update(id, {
      actAt: interval.start,
      plannedEndAt: interval.end,
      allDay,
    });
  }

  updateAllDay(id: string, newDate: string) {
    return this.update(id, {
      actAt: newDate,
      plannedEndAt: null,
      allDay: true,
    });
  }

  updateSchedule(
    id: string,
    plannedDate: { actAt: string; plannedEndAt: string; allDay: boolean },
    assigneeIds: string[],
  ): Observable<void> {
    return this.update(id, {
      actAt: plannedDate.actAt,
      plannedEndAt: plannedDate.plannedEndAt,
      allDay: plannedDate.allDay,
      assigneeIds,
    });
  }

  update(id: string, change: any): Observable<void> {
    return this.http.patch<void>(
      this.config.url + 'tickets/multimachinesupports/' + id,
      change,
    );
  }

  export(ids: string[]): Observable<Blob> {
    return this.http.post(
      this.config.url + 'tickets/multimachinesupports/export',
      ids,
      { responseType: 'blob' },
    );
  }
}

export function isBlockedWithoutMotiveError(err: HttpErrorResponse): boolean {
  return isSingleErrorCode(err, ERROR_WORK_ORDER_BLOCKED_WITHOUT_MOTIVE);
}

export const ERROR_WORK_ORDER_BLOCKED_WITHOUT_MOTIVE =
  'WORK_ORDER_BLOCKED_WITHOUT_MOTIVE';

export function isCompanyChangeBlockedByInstallationsError(
  err: HttpErrorResponse,
): boolean {
  return isSingleErrorCode(
    err,
    ERROR_WORK_ORDER_COMPANY_CHANGE_BLOCKED_BY_INSTALLATIONS,
  );
}

export const ERROR_WORK_ORDER_COMPANY_CHANGE_BLOCKED_BY_INSTALLATIONS =
  'WORK_ORDER_COMPANY_CHANGE_BLOCKED_BY_INSTALLATIONS';
